// ABOUTME: A picture on the whole screen, over everything open: it closes on Escape, on its cross or on the dark
// ABOUTME: around it, holds keyboard focus while open, and its Escape closes it alone (the shared overlay stack).
import { useCallback, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { useFocusTrap } from "@shared/hooks/useFocusTrap"
import { useZIndex } from "@shared/hooks/useZIndex"
import { CSpinner } from "./CSpinner"
import { useEscapeOwnership } from "./escapeStack"

interface CPictureViewerProps {
  /** The picture's address; null while none is open. */
  src: string | null
  alt: string
  closeLabel: string
  onClose: () => void
  /** The picture did not load: its owner may mend it — a fresh address — and show it again. */
  onImageError?: () => void
  /** Being mended: a spinner in its place. */
  isPending?: boolean
  /** Given up on: this text in its place. */
  failureText?: string | null
  /** A new key loads the picture again, even from the same address. */
  imageKey?: string
}

export const CPictureViewer = (props: CPictureViewerProps) => {
  const { src, alt, closeLabel, onClose, onImageError, isPending = false, failureText = null, imageKey } = props
  const isOpen = src !== null
  const zIndex = useZIndex(isOpen)
  const isTopmostOverlay = useEscapeOwnership(isOpen, zIndex)
  const panelRef = useRef<HTMLDivElement | null>(null)
  // Tab must not reach, nor Enter press, what lies under the picture; the opener gets focus back on close.
  useFocusTrap(panelRef, isOpen && zIndex !== 0)

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || !isTopmostOverlay()) return
      // The window the picture was opened from stays: one Escape, one overlay.
      event.preventDefault()
      onClose()
    },
    [isTopmostOverlay, onClose],
  )
  useEffect(() => {
    if (!isOpen) return
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen, handleEscape])

  if (src === null || zIndex === 0) return null
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      tabIndex={-1}
      className="fixed inset-0 flex items-center justify-center bg-black/90 p-4 outline-none"
      style={{ zIndex }}
    >
      {/* The dark around the picture closes it, as a tap outside does on a phone. */}
      <button
        type="button"
        aria-label={closeLabel}
        tabIndex={-1}
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      {failureText !== null ? (
        <p className="relative text-sm text-white">{failureText}</p>
      ) : isPending ? (
        <CSpinner
          size="lg"
          color="gray"
        />
      ) : (
        <img
          key={imageKey}
          src={src}
          alt={alt}
          onError={onImageError}
          className="relative max-h-full max-w-full object-contain"
        />
      )}
      <button
        type="button"
        aria-label={closeLabel}
        title={closeLabel}
        onClick={onClose}
        className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
      >
        <X className="h-6 w-6" />
      </button>
    </div>,
    document.body,
  )
}
